import { yupResolver } from '@hookform/resolvers/yup'
import { map } from 'lodash-es'
import { useCallback, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { PageTitle } from '@/components'
import { FormSelect } from '@/components/Form'
import { Button, Checkbox, Input, Modal } from '@/components/ui'
import Loader from '../../../../common/components/Loader'
import { UserGroups } from '../../../../common/models'
import FormGroup from '../../../../core/components/FormGroup'
import { getAllUserGroups } from '../../../../core/services/permissions'
import { createUser } from '../../../../core/services/users'
import { MessageBox } from '../../messages/MessageBox'
import SelectField from '@/components/select-field'

interface IFormValues {
  name: string
  phone: string
  email: string
  password: string
  userGroup: string
  active: boolean
}

export interface AddUserProps {
  visible: boolean
  setVisible: (show: boolean) => void
  onSuccess?: () => void
  disabled: boolean
}

export const AddUser = ({
  visible,
  setVisible,
  onSuccess,
  disabled,
}: AddUserProps): JSX.Element => {
  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const [userGroups, setUserGroups] = useState<UserGroups[]>([])

  const getData = useCallback(async () => {
    try {
      setLoading(true)
      const { data: userGroupsData, status } = await getAllUserGroups({
        size: 100,
      })

      if (status === 200) {
        setUserGroups(userGroupsData.content)
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (visible) {
      getData()
    }
    return () => {
      reset()
    }
  }, [visible, getData])

  const handleClose = () => {
    setVisible(false)
  }

  const schema = yup.object().shape({
    name: yup
      .string()
      .required('Name is required')
      .test('name', 'Name is required', (value) => {
        if (value) {
          return value.split(' ').length === 2
        }
        return false
      }),
    phone: yup.string().required('Phone is required'),
    email: yup.string().required('Email is required'),
    password: yup.string().required('Password is required'),
    userGroup: yup.string().required('User Group is required'),
    active: yup.boolean().oneOf([true, false]).required('Active is required'),
  })

  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
    control,
  } = useForm<IFormValues>({
    mode: 'onChange',
    resolver: yupResolver<IFormValues>(schema),
    defaultValues: {
      active: true,
    },
  })

  const submit = async (values: IFormValues) => {
    const payload = {
      email: values.email,
      firstName: values.name.split(' ')[0],
      lastName: values.name.split(' ')[1],
      phone: values.phone,
      active: values.active,
      userGroupId: values.userGroup,
    }
    try {
      setSubmitting(true)
      const { status } = await createUser({
        ...payload,
        password: values.password,
      })
      if (status === 204) {
        handleClose()
        onSuccess?.()
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      setIsError(true)
      setShowMessageBox(true)
      setMessage(e?.response?.data?.developerMessage)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      {loading && <Loader />}
      <Modal
        open={visible}
        onOpenChange={(open) => {
          if (!open) handleClose()
        }}
        size="lg"
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle title="Users" subtitle="Add New User" />
          </div>
        }
        footer={
          <div className="flex w-full items-center justify-end gap-2">
            <Button onClick={handleClose} type="button" variant="secondary">
              Cancel
            </Button>
            <Button
              disabled={disabled || submitting}
              form="add-new-user-form"
              type="submit"
              variant="primary"
              loading={submitting}
            >
              Save
            </Button>
          </div>
        }
      >
        <form
          id="add-new-user-form"
          onSubmit={handleSubmit(submit)}
          className="space-y-7"
        >
          <div className="flex flex-col gap-4">
            <FormGroup label="Name" error={errors?.name?.message}>
              <Input
                {...register('name')}
                type="text"
                placeholder="Enter full name (First Last)"
                isInvalid={!!errors.name?.message}
                disabled={disabled}
              />
            </FormGroup>

            <FormGroup label="Phone" error={errors?.phone?.message}>
              <Input
                {...register('phone')}
                type="text"
                placeholder="Enter phone number"
                isInvalid={!!errors.phone?.message}
                disabled={disabled}
              />
            </FormGroup>

            <FormGroup label="Email" error={errors?.email?.message}>
              <Input
                {...register('email')}
                type="email"
                placeholder="Enter email address"
                isInvalid={!!errors.email?.message}
                autoComplete="off"
                disabled={disabled}
              />
            </FormGroup>

            <FormGroup label="Password" error={errors?.password?.message}>
              <Input
                {...register('password')}
                type="password"
                placeholder="Enter password"
                isInvalid={!!errors.password?.message}
                autoComplete="new-password"
                disabled={disabled}
              />
            </FormGroup>
            <Controller
              name="userGroup"
              control={control}
              render={({ field }) => (
                <FormGroup
                  label="User Group"
                  error={errors?.userGroup?.message}
                >
                  <SelectField
                    outline
                    error={errors?.userGroup?.message}
                    value={field.value}
                    onValueChange={field.onChange}
                    options={map(userGroups, (group) => ({
                      value: group.id,
                      label: group.name,
                    }))}
                    placeholder="Select Group"
                    disabled={disabled}
                  />
                </FormGroup>
              )}
            />
            <FormGroup
              label="Active"
              direction="row"
              labelClassName="whitespace-nowrap"
            >
              <Controller
                name="active"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    checked={Boolean(field.value)}
                    onCheckedChange={(checked) =>
                      field.onChange(Boolean(checked))
                    }
                    disabled={disabled}
                  />
                )}
              />
            </FormGroup>
          </div>
        </form>
      </Modal>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
